
import React from 'react';
import { Cookie, Users, Save, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { GdprTabProps, ConsentDataType } from './types';

const GDPRSettingsTab: React.FC<GdprTabProps> = ({
  gdprSettings,
  onGdprChange,
  onSaveGdprSettings,
  updateGdprSettingsMutation,
  userConsents,
  deleteOldConsentsMutation,
}) => {
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Cookie className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold">GDPR Compliance</h3>
          </div>
          <Button 
            onClick={onSaveGdprSettings} 
            disabled={updateGdprSettingsMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4 mr-2" />
            Save GDPR Settings
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="gdpr_enabled">Enable GDPR Compliance</Label>
              <p className="text-sm text-gray-600">Show consent banner and track user preferences</p>
            </div>
            <Switch
              id="gdpr_enabled"
              checked={gdprSettings.gdpr_enabled}
              onCheckedChange={(checked) => onGdprChange('gdpr_enabled', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="consent_mode_enabled">Google Consent Mode v2</Label>
              <p className="text-sm text-gray-600">Enable Google's consent mode for ads and analytics</p>
            </div>
            <Switch
              id="consent_mode_enabled"
              checked={gdprSettings.consent_mode_enabled}
              onCheckedChange={(checked) => onGdprChange('consent_mode_enabled', checked)}
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="consent_banner_text">Consent Banner Text</Label>
            <Textarea
              id="consent_banner_text"
              value={gdprSettings.consent_banner_text}
              onChange={(e) => onGdprChange('consent_banner_text', e.target.value)}
              placeholder="We use cookies and similar technologies..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="privacy_policy_url">Privacy Policy URL</Label>
            <Input
              id="privacy_policy_url"
              value={gdprSettings.privacy_policy_url}
              onChange={(e) => onGdprChange('privacy_policy_url', e.target.value)}
              placeholder="/privacy"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cookie_policy_url">Cookie Policy URL</Label>
            <Input
              id="cookie_policy_url"
              value={gdprSettings.cookie_policy_url}
              onChange={(e) => onGdprChange('cookie_policy_url', e.target.value)}
              placeholder="/cookies"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="data_retention_days">Data Retention (Days)</Label>
            <Input
              id="data_retention_days"
              type="number"
              min="1"
              max="2555"
              value={gdprSettings.data_retention_days}
              onChange={(e) => onGdprChange('data_retention_days', parseInt(e.target.value) || 730)}
              placeholder="730"
            />
            <p className="text-sm text-gray-600">How long to keep consent records (GDPR recommends max 25 months)</p>
          </div>
        </div>
      </Card>

      {/* User Consents Analytics */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold">User Consents</h3>
          </div>
          <Button
            variant="outline"
            onClick={() => deleteOldConsentsMutation.mutate()}
            disabled={deleteOldConsentsMutation.isPending}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Old Records
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Session ID</TableHead>
                <TableHead>Consent Date</TableHead>
                <TableHead>Analytics</TableHead>
                <TableHead>Advertising</TableHead>
                <TableHead>Preferences</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {userConsents?.slice(0, 10).map((consent) => {
                const consentData = consent.consent_data as unknown as ConsentDataType;
                return (
                  <TableRow key={consent.id}>
                    <TableCell className="font-mono text-xs">
                      {consent.user_session_id.slice(-8)}...
                    </TableCell>
                    <TableCell>
                      {new Date(consent.consent_timestamp).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={consentData.analytics ? 'default' : 'secondary'}>
                        {consentData.analytics ? 'Granted' : 'Denied'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={consentData.advertising ? 'default' : 'secondary'}>
                        {consentData.advertising ? 'Granted' : 'Denied'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={consentData.preferences ? 'default' : 'secondary'}>
                        {consentData.preferences ? 'Granted' : 'Denied'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!userConsents?.length && (
            <div className="text-center py-8 text-gray-500">
              No consent records found
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default GDPRSettingsTab;
